import { ROLES } from "@/common/permissions";
import {
  DASHBOARD_FUNDING_ROUTE,
  DASHBOARD_GUEST_FLOW_ROUTE,
  DASHBOARD_HOME_ROUTE,
  DASHBOARD_REPORTS_ROUTE,
} from "@/common/routes.common";

/** Dashboard pages the Mezza Hub can hide per merchant. Values match the API contract. */
export const DASHBOARD_PAGES = {
  overview: "overview",
  websiteLeads: "websiteLeads",
  funding: "funding",
  guestFlow: "guestFlow",
  reports: "reports",
} as const;

export type DashboardPageKey = (typeof DASHBOARD_PAGES)[keyof typeof DASHBOARD_PAGES];

/** Overview cards, tiles and charts the Mezza Hub can hide per merchant. Values match the API contract. */
export const DASHBOARD_METRICS = {
  capital: "capital",
  sales: "sales",
  orders: "orders",
  members: "members",
  averageBill: "averageBill",
  tips: "tips",
  guests: "guests",
  visitsPerMember: "visitsPerMember",
  perksUsed: "perksUsed",
  cashbackEarned: "cashbackEarned",
  salesTrend: "salesTrend",
  memberMix: "memberMix",
  peakTimes: "peakTimes",
  locationsBreakdown: "locationsBreakdown",
  /** Delta badges on the KPI tiles and the previous-period overlay on the trend chart. */
  periodComparison: "periodComparison",
} as const;

export type DashboardMetricKey = (typeof DASHBOARD_METRICS)[keyof typeof DASHBOARD_METRICS];

/** Reports on the Reports page the Mezza Hub can hide per merchant. Values match the API contract. */
export const DASHBOARD_REPORTS = {
  orders: "orders",
  tips: "tips",
  statementOfAccount: "statementOfAccount",
} as const;

export type DashboardReportKey = (typeof DASHBOARD_REPORTS)[keyof typeof DASHBOARD_REPORTS];

/** Pages limited to a role and above, on top of the Hub's visibility settings. The API enforces the same. */
export const DASHBOARD_PAGE_MIN_ROLE: Readonly<Partial<Record<DashboardPageKey, ROLES>>> = {
  reports: ROLES.MANAGER,
};

/** Metrics that live in the period-driven Activity section of the Overview. */
export const DASHBOARD_ACTIVITY_METRICS: readonly DashboardMetricKey[] = [
  DASHBOARD_METRICS.sales,
  DASHBOARD_METRICS.orders,
  DASHBOARD_METRICS.members,
  DASHBOARD_METRICS.averageBill,
  DASHBOARD_METRICS.tips,
  DASHBOARD_METRICS.guests,
  DASHBOARD_METRICS.visitsPerMember,
  DASHBOARD_METRICS.perksUsed,
  DASHBOARD_METRICS.cashbackEarned,
  DASHBOARD_METRICS.salesTrend,
  DASHBOARD_METRICS.memberMix,
  DASHBOARD_METRICS.peakTimes,
  DASHBOARD_METRICS.locationsBreakdown,
];

/** Route of each dashboard page, in sidebar order (the first visible one is the fallback landing page). */
export const DASHBOARD_PAGE_ROUTES: Readonly<Record<DashboardPageKey, string>> = {
  overview: DASHBOARD_HOME_ROUTE,
  websiteLeads: `${DASHBOARD_HOME_ROUTE}/website-leads`,
  funding: DASHBOARD_FUNDING_ROUTE,
  guestFlow: DASHBOARD_GUEST_FLOW_ROUTE,
  reports: DASHBOARD_REPORTS_ROUTE,
};

export const DASHBOARD_PAGE_ORDER: readonly DashboardPageKey[] = [
  DASHBOARD_PAGES.overview,
  DASHBOARD_PAGES.websiteLeads,
  DASHBOARD_PAGES.funding,
  DASHBOARD_PAGES.guestFlow,
  DASHBOARD_PAGES.reports,
];
